/**
 * Facility analytics API client for the business app.
 *
 * Single source of truth for the operator /dashboard KPI strip. Wraps
 *   GET /analytics/facilities/{facilityId}/dashboard
 * behind the BFF proxy. Mirrors libs/orgAnalyticsApi.ts (httpClient.get +
 * encodeURIComponent). Consumed by:
 *   - dashboard/operator-dashboard.tsx (facility-ops home KPI strip)
 *
 * Every KPI rendered on /dashboard comes from a field on this response — the
 * frontend is a thin presentation layer (the only client-side math is the
 * guarded utilization %, derived from courts_in_use / courts_total). No
 * fabricated numbers; an error surfaces an honest empty/error state instead.
 *
 * The response type is the generated FacilityDashboardStatsResponse (source of
 * truth: backend Pydantic → openapi.json → generated/api.ts). The backend also
 * returns nullable `currency` for the money field in that same generated schema.
 * Empty or failed money must not be relabelled USD zero by a consumer.
 */
import { httpClient } from "@bw/shared/api";
import type {
  FacilityBookingCountResponse,
  FacilityPeakHoursResponse,
  FacilityRevenueResponse,
  FacilityUtilizationResponse,
  PeakHourBucket,
  Schema,
  components,
} from "@bw/shared/types/generated";

// Analytics endpoint responses now carry a backend response_model (#1393), so
// the shapes are the generated aliases — NOT hand-mirrored here. Re-export them
// so consumers keep importing from this lib (the app's analytics facade).
export type {
  FacilityBookingCountResponse,
  FacilityPeakHoursResponse,
  FacilityRevenueResponse,
  FacilityUtilizationResponse,
  PeakHourBucket,
};

/**
 * Aggregated stats for a facility dashboard.
 *
 * `today_revenue` is a Pydantic Decimal (`number | string` at runtime) — narrow
 * with formatMoney/parseDecimal, never do arithmetic on it directly.
 * `revenue_delta_pct` / `sessions_delta_pct` are ALWAYS null today (the backend
 * does not compute period-over-period deltas yet) — they must never surface as
 * fabricated delta arrows in the UI.
 */
export type FacilityDashboardStatsResponse =
  Schema["FacilityDashboardStatsResponse"];

/** The facility-ops home KPI strip (today's revenue, sessions, occupancy, …). */
export function getFacilityDashboard(
  facilityId: string,
): Promise<FacilityDashboardStatsResponse> {
  return httpClient.get<FacilityDashboardStatsResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/dashboard`,
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Analytics-workspace clients (/analytics/revenue + /analytics/bookings).
//
// These wrap the facility analytics summary/trend/occupancy endpoints. The
// summary routes (revenue/utilization/peak-hours/booking-count) now carry a
// backend response_model (#1393), so their response types are the generated
// aliases re-exported above. Trends use the generated response including ledger
// currency and an explicit reporting end date. Occupancy retains its separate
// presentation contract.
//
// THIN-layer contract: every field is read AS-IS. In particular,
// `utilization_percent` is ALREADY a 0–100 value — callers must render it
// directly and must NEVER multiply by 100 (e.g. do not pass it through
// formatPercent, which scales a 0–1 fraction).
// ────────────────────────────────────────────────────────────────────────────

/** One daily trend point. `revenue` is a branded Money (Decimal string). */
export type TrendDataPoint = Schema["TrendDataPoint"];

/** Daily revenue / booking trends for the period. */
export type FacilityTrendsResponse = Schema["FacilityTrendResponse"];
export type FacilityRevenueBySourceResponse =
  Schema["FacilityRevenueBySourceResponse"];
export type OperationalInsightsComparisonRequest =
  Schema["OperationalInsightsComparisonRequest"];
export type OperationalInsightsComparisonResponse =
  Schema["OperationalInsightsComparisonResponse"];
export type OperationalInsightsDemandOutlookResponse =
  Schema["OperationalInsightsDemandOutlookResponse"];
export type ProgramInsightsRequest = Schema["ProgramInsightsRequest"];
export type ProgramInsightsResponse = Schema["ProgramInsightsResponse"];
export type GrowthInsightsRequest =
  components["schemas"]["GrowthInsightsRequest"];
export type GrowthInsightsResponse =
  components["schemas"]["GrowthInsightsResponse"];
export type InstructorInsightsRequest =
  components["schemas"]["InstructorInsightsRequest"];
export type InstructorInsightsResponse =
  components["schemas"]["InstructorInsightsResponse"];
export type ServiceRetentionInsightsRequest =
  components["schemas"]["ServiceRetentionInsightsRequest"];
export type ServiceRetentionInsightsResponse =
  components["schemas"]["ServiceRetentionInsightsResponse"];

/** One hour-row of court occupancy for a single date. utilization_percent is 0–100. */
export interface OccupancyRow {
  /** Hour of day, 0–23. */
  hour: number;
  total_courts: number;
  booked_courts: number;
  /** ALREADY 0–100 — render AS-IS, never ×100. */
  utilization_percent: number;
}

/** Total period revenue for a facility over [start, end] (ISO dates). */
export function getFacilityRevenue(
  facilityId: string,
  start: string,
  end: string,
): Promise<FacilityRevenueResponse> {
  return httpClient.get<FacilityRevenueResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/revenue` +
      `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  );
}

export function getFacilityRevenueBySource(
  facilityId: string,
  start: string,
  end: string,
): Promise<FacilityRevenueBySourceResponse> {
  return httpClient.get<FacilityRevenueBySourceResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/revenue-by-source` +
      `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  );
}

/** Booking count for a facility over [start, end] (ISO dates). */
export function getFacilityBookingCount(
  facilityId: string,
  start: string,
  end: string,
): Promise<FacilityBookingCountResponse> {
  return httpClient.get<FacilityBookingCountResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/booking-count` +
      `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  );
}

/** Average utilization (0–100) for a facility over [start, end] (ISO dates). */
export function getFacilityUtilization(
  facilityId: string,
  start: string,
  end: string,
): Promise<FacilityUtilizationResponse> {
  return httpClient.get<FacilityUtilizationResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/utilization` +
      `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  );
}

/** Peak-hours distribution for a facility over [start, end] (ISO dates). */
export function getFacilityPeakHours(
  facilityId: string,
  start: string,
  end: string,
): Promise<FacilityPeakHoursResponse> {
  return httpClient.get<FacilityPeakHoursResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/peak-hours` +
      `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  );
}

/** Licensed server-computed planning comparison; existing analytics stay baseline. */
export function compareFacilityOperatingPeriods(
  facilityId: string,
  request: OperationalInsightsComparisonRequest,
): Promise<OperationalInsightsComparisonResponse> {
  return httpClient.post<OperationalInsightsComparisonResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/insights/comparisons`,
    request,
  );
}

/** Licensed, backtested court-demand projection; unsupported projections stay hidden. */
export function getFacilityDemandOutlook(
  facilityId: string,
): Promise<OperationalInsightsDemandOutlookResponse> {
  return httpClient.post<OperationalInsightsDemandOutlookResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/insights/demand-outlook`,
  );
}

/** Licensed Program attendance/delivery aggregates; learner identities stay private. */
export function compareFacilityPrograms(
  facilityId: string,
  request: ProgramInsightsRequest,
): Promise<ProgramInsightsResponse> {
  return httpClient.post<ProgramInsightsResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/insights/programs`,
    request,
  );
}

/** Licensed, privacy-bounded reservation retention and campaign return facts. */
export function compareFacilityCustomerGrowth(
  facilityId: string,
  request: GrowthInsightsRequest,
): Promise<GrowthInsightsResponse> {
  return httpClient.post<GrowthInsightsResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/insights/growth`,
    request,
  );
}

/** Licensed club-certified instructor workload facts; no quality or payroll scoring. */
export function compareFacilityInstructorDelivery(
  facilityId: string,
  request: InstructorInsightsRequest,
): Promise<InstructorInsightsResponse> {
  return httpClient.post<InstructorInsightsResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/insights/instructors`,
    request,
  );
}

/** Licensed service-by-service repeat-participation facts; no customer identities. */
export function compareFacilityServiceRetention(
  facilityId: string,
  request: ServiceRetentionInsightsRequest,
): Promise<ServiceRetentionInsightsResponse> {
  return httpClient.post<ServiceRetentionInsightsResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/insights/service-retention`,
    request,
  );
}

/** Daily revenue / booking trends for a facility over a named period. */
export function getFacilityTrends(
  facilityId: string,
  period: string,
  endDate?: string,
): Promise<FacilityTrendsResponse> {
  return httpClient.get<FacilityTrendsResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/trends` +
      `?period=${encodeURIComponent(period)}` +
      (endDate ? `&end_date=${encodeURIComponent(endDate)}` : ""),
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Settlement (Earnings) client — /analytics/earnings.
//
// A facility OWNER's payout view: gross collected → platform-fee deduction →
// refunds → NET OWED, by payout period. Wraps
//   GET /analytics/facilities/{facility_id}/settlements?periods=N
// The response shape is the generated SettlementSummaryResponse (source of
// truth: backend Pydantic → openapi.json → generated/api.ts), mirroring how the
// platform finance api types getPlatformSettlements. Re-exported so the
// workspace imports the shapes from this lib (the app's analytics facade).
// ────────────────────────────────────────────────────────────────────────────

export type SettlementSummaryResponse =
  components["schemas"]["SettlementSummaryResponse"];
export type SettlementPeriodResponse =
  components["schemas"]["SettlementPeriodResponse"];
export type PayoutHistoryResponse =
  components["schemas"]["PayoutHistoryResponse"];
export type PayoutLedgerEntryResponse =
  components["schemas"]["PayoutLedgerEntryResponse"];

/**
 * Settlement summary for a facility over the last `periods` payout windows.
 *
 * FACILITY/OWNER framing: `total_net_owed` is what THIS facility will be paid
 * (the headline); `total_platform_fees` is the platform's deduction. When the
 * window spans multiple currencies the backend sets `currency: null` and the
 * aggregate totals are not expressible as a single figure — the caller falls
 * back to the per-period rows (each carries its own `currency`).
 */
export function getFacilitySettlements(
  facilityId: string,
  periods = 6,
): Promise<SettlementSummaryResponse> {
  return httpClient.get<SettlementSummaryResponse>(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/settlements` +
      `?periods=${periods}`,
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Payout-history client — /analytics/orgs/{org_id}/payouts.
//
// An ORG OWNER's record of actual Stripe payouts (what HAS been / will be
// disbursed to their bank), distinct from the settlement view above (what is
// OWED, by accrual period). Wraps
//   GET /analytics/orgs/{org_id}/payouts?status=&limit=&offset=
// (org owner / internal platform_admin only — a non-owner facility manager gets
// 403). The response shape is the generated PayoutHistoryResponse, mirroring how
// the platform finance api types getPlatformPayouts. Re-exported above so the
// component imports the shapes from this lib (the app's analytics facade).
//
// THIN layer: every field — amount (a SIGNED Money), status, dates, failure_code
// — is rendered AS-IS by the component. No client-side money math.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Payout history for an org over an optional status filter / page window.
 *
 * `amount` on each entry is a SIGNED Money (Pydantic Decimal → `number | string`
 * at runtime) — narrow with formatMoney, never do arithmetic on it directly.
 */
export function getOrgPayouts(
  orgId: string,
  opts?: { status?: string; limit?: number; offset?: number },
): Promise<PayoutHistoryResponse> {
  const params = new URLSearchParams();
  if (opts?.status) params.set("status", opts.status);
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.offset != null) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return httpClient.get<PayoutHistoryResponse>(
    `/analytics/orgs/${encodeURIComponent(orgId)}/payouts` +
      (qs ? `?${qs}` : ""),
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Reconciliation client — /analytics/orgs/{org_id}/reconciliation.
//
// An ORG OWNER's reconciliation view: per payout PERIOD, the computed net owed
// (accrual) vs what was actually PAID OUT (the Stripe payout ledger), with the
// SIGNED delta and a reconciliation_status. Answers "do the books match the
// money that actually moved?" — sitting beside the payout ledger (what was paid)
// and the settlement view (what is owed). Wraps
//   GET /analytics/orgs/{org_id}/reconciliation?periods=N
// (org owner / internal platform_admin only — a non-owner facility manager gets
// 403). The response shape is the generated ReconciliationResponse, mirroring how
// the platform finance api types getPlatformPayouts. Re-exported below so the
// component imports the shapes from this lib (the app's analytics facade).
//
// THIN layer: every field — computed_net_owed / paid_out / delta (each Money,
// delta SIGNED), reconciliation_status, the unmatched/failed payout counts — is
// rendered AS-IS by the component. No client-side money math (a client re-compute
// of the delta could diverge from the backend and lie).
// ────────────────────────────────────────────────────────────────────────────

export type ReconciliationResponse =
  components["schemas"]["ReconciliationResponse"];
export type PeriodReconciliationResponse =
  components["schemas"]["PeriodReconciliationResponse"];

/**
 * Reconciliation for an org over the last `periods` payout windows.
 *
 * Each period's `delta` is a SIGNED Money (Pydantic Decimal → `number | string`
 * at runtime) — narrow with formatMoney, never do arithmetic on it directly. The
 * `reconciliation_status` token is rendered via the shared status mapping, never
 * a fabricated/derived verdict.
 */
export function getOrgReconciliation(
  orgId: string,
  opts?: { periods?: number },
): Promise<ReconciliationResponse> {
  const params = new URLSearchParams();
  if (opts?.periods != null) params.set("periods", String(opts.periods));
  const qs = params.toString();
  return httpClient.get<ReconciliationResponse>(
    `/analytics/orgs/${encodeURIComponent(orgId)}/reconciliation` +
      (qs ? `?${qs}` : ""),
  );
}

/** Per-hour court occupancy for a facility on a single ISO date (YYYY-MM-DD). */
export function getFacilityOccupancy(
  facilityId: string,
  date: string,
): Promise<OccupancyRow[]> {
  return httpClient.get<OccupancyRow[]>(
    `/staff/facilities/${encodeURIComponent(facilityId)}/occupancy` +
      `?date=${encodeURIComponent(date)}`,
  );
}

/**
 * Transaction CSV export for a facility over [start, end] (ISO dates).
 *
 * Backend: GET /analytics/facilities/{facility_id}/export?start=&end=&format=csv
 * returns a text/csv body (PlainTextResponse, Content-Disposition: attachment;
 * RBAC facility:read_internal). Uses the TEXT path — a CSV body must NEVER be
 * parsed as JSON — and returns the raw string VERBATIM for the caller to offer
 * as a download. THIN layer: no parsing/rebuilding of the CSV here.
 */
export function exportFacilityTransactionsCsv(
  facilityId: string,
  start: string,
  end: string,
): Promise<string> {
  return httpClient.getText(
    `/analytics/facilities/${encodeURIComponent(facilityId)}/export` +
      `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&format=csv`,
  );
}
